import { ContentRow, Metadata } from "@dropbox/dash-component-library";
import { Text } from "@dropbox/dig-components/typography";
import { Link } from "@mirage/link/Link";
import { generateSearchURL } from "@mirage/search/hooks/useQueryParams";
import { openURL } from "@mirage/service-platform-actions";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import i18n from "@mirage/translations";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { ChatSource } from "../../types";
import { getSourceRowParts } from "../compose-sources/ComposeSourceRow";
import styles from "./AllSourcesList.module.css";
import { SourceActionBar } from "./SourceActionBar";

// We're only displaying the top few results for now.
// user has to click "See all" to get the rest
const MAX_DISPLAYED_SOURCES = 4;

export const AllSourcesList = ({
  sources,
  query,
}: {
  sources: ChatSource[];
  query?: string;
}) => {
  const isMobileSize = useIsMobileSize();
  const navigate = useNavigate();

  const handleSeeAllResults = useCallback(() => {
    // TODO (matto): Report PAP event
    navigate(generateSearchURL(query ?? ""));
  }, [query, navigate]);

  return (
    <>
      <div className={styles.header}>
        <Text size="large" color="subtle">
          {i18n.t("top_results")}
        </Text>
        <Text size="large" color="subtle">
          <Link
            variant="monochromatic"
            onClick={handleSeeAllResults}
            href={"#"}
          >
            {i18n.t("see_all_chat_results")}
          </Link>
        </Text>
      </div>
      <div className={styles.sources}>
        {sources.slice(0, MAX_DISPLAYED_SOURCES).map((source, i) => {
          const sourceRowParts = getSourceRowParts(source, {
            hideBackground: false,
          });
          const url = sourceRowParts?.mirageResult.url;
          if (!sourceRowParts || !url) {
            return null;
          }

          const { titleString, icon, sizeOrMtimeString } = sourceRowParts;

          return (
            <ContentRow
              key={i}
              isMobile={isMobileSize}
              paddingSize="medium"
              onClick={() => {
                if (source.type !== "local_file_content") {
                  openURL(url);
                }
              }}
              visibleUrl={url}
              withStartAccessory={icon}
              withLabel={<Text variant="label">{titleString}</Text>}
              withMeta={
                <Metadata size="small">
                  <Metadata.Item>
                    <Metadata.Label>{sizeOrMtimeString}</Metadata.Label>
                  </Metadata.Item>
                </Metadata>
              }
              withActions={
                <SourceActionBar
                  source={source}
                  title={titleString}
                  url={url}
                />
              }
            />
          );
        })}
      </div>
    </>
  );
};
